/**
 * interfaces
 */
import { IDataTableResponse } from '../interfaces';
import { IDataTableResponseModel } from './interfaces';

/**
 * @class DataTableResponseModel
 * @description View model for one page of a lazy datatable response.
 *
 * Wraps the raw {@link IDataTableResponse} payload — the object carried inside
 * the standard `IResponse` envelope — and exposes its counts as camelCase
 * getters, so consumers never read the snake_case wire fields directly.
 *
 * Every count getter falls back to `0`, and a non-array `data` degrades to an
 * empty array, so a malformed response never reaches the pager or row mapping.
 *
 * Row mapping is left to the caller: features map DTOs to their own view
 * models in the facade's `.pipe(map(...))`, keeping this model purely about
 * the response envelope.
 *
 * @template TData - Row type carried by the payload.
 *
 * @example
 * ```typescript
 * // In the facade layer
 * this._dataTableSource
 *   .dataTableSourceHandler<IGridBillingAccountDto>(endpoint, request)
 *   .pipe(
 *     map((response) => response.data.map((dto) => new GridBillingAccountModel(dto))),
 *   );
 * ```
 */
export class DataTableResponseModel<TData> implements IDataTableResponseModel<TData> {

    /**
     * @param dto - Raw datatable payload carried inside the `IResponse` envelope.
     */
    constructor(public readonly dto: IDataTableResponse<TData>) {}

    /** @inheritdoc */
    public get data(): TData[] {
        return Array.isArray(this.dto?.data) ? this.dto.data : [];
    }

    /** @inheritdoc */
    public get totalDataCount(): number {
        return this.dto?.total_data_count ?? 0;
    }

    /** @inheritdoc */
    public get filteredDataCount(): number {
        return this.dto?.filtered_data_count ?? 0;
    }

    /** @inheritdoc */
    public get returnedDataCount(): number {
        return this.dto?.returned_data_count ?? 0;
    }

    /** @inheritdoc */
    public get paginationCount(): number {

        // `filtered_data_count` is the only count that tracks the active
        // search/filter, so it — not the unfiltered total — is what the pager
        // must size itself against. A `0` here is meaningful (a filter that
        // matched nothing), hence the presence check rather than `||`.
        const filteredDataCount = this.dto?.filtered_data_count;

        return typeof filteredDataCount === 'number' ? filteredDataCount : this.totalDataCount;
    }

    /** @inheritdoc */
    public get isEmpty(): boolean {
        return this.data.length === 0;
    }

}
